'use client';

import { useEffectEvent } from "@floating-ui/react/utils";
import { cx } from "@linaria/core";
import { styled } from "@linaria/react";
import React, { ReactNode, startTransition, useEffect, useMemo } from "react";
import "./ShinyBorderCard.linaria.module.css!=!./ShinyBorderCard?./ShinyBorderCard.linaria.module.css?css=H4sIAAAAAAAAA1VPS4qEMBTc9ykeiKDQT9TgYpLNXOXlQ4yjicT0TGjx7oN093yoRdWiiqpqZEfT5vO0r6S185b3axaS1IeN4eY1qjCHyIt+OCEQVxOV8Ykxxoe2/Ot0C1nDI2lHM9qTjU8VKBfVbIASfFKsEF+F2NZXKIgIhvIKBWPsafhtqGtxvC9GO4JNRWM8kNdQLZTxy+k0cnhruzXXe/Pz4t/AfijFcVwamafb3er9EerathSjcXZMDy1Dxs3dz/MyRG0iypDFcfkGH0AG1hwBAAA="
function useGradientPosition(ref: React.RefObject<HTMLDivElement | null>) {
  const [targetMousePosition, setTargetMousePosition] = React.useState({
    x: -500,
    y: -500
  });
  const [smoothedMousePosition, setSmoothedMousePosition] = React.useState({
    x: -500,
    y: -500
  });
  const [rerender, setRerender] = React.useState({}); // eslint-disable-line @typescript-eslint/no-unused-vars

  const updateMousePosition = useEffectEvent((ev: MouseEvent | Event | UIEvent) => {
    if (ev && 'clientX' in ev) setTargetMousePosition({
      x: ev.clientX,
      y: ev.clientY
    });else setRerender({}); // force rerender on resize/scroll
  });
  useEffect(() => {
    if (targetMousePosition.x === smoothedMousePosition.x && targetMousePosition.y === smoothedMousePosition.y) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        startTransition(() => {
          setSmoothedMousePosition({
            x: Math.round(smoothedMousePosition.x + (targetMousePosition.x - smoothedMousePosition.x) * 0.24 * (window.innerHeight / window.innerWidth) ** 2),
            // I don't know why squaring the ratio here looks so good, but by golly does it look good
            y: Math.round(smoothedMousePosition.y + (targetMousePosition.y - smoothedMousePosition.y) * 0.24 * (window.innerHeight / window.innerWidth))
          });
        });
      });
    });
  }, [targetMousePosition, smoothedMousePosition, updateMousePosition]);
  useEffect(() => {
    const div = ref.current;
    if (!div) return;
    window.addEventListener('mousemove', updateMousePosition);
    window.addEventListener('resize', updateMousePosition);
    window.addEventListener('scroll', updateMousePosition, true);
    const resizeObserver = new ResizeObserver(() => setRerender({}));
    resizeObserver.observe(div);
    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
      window.removeEventListener('resize', updateMousePosition);
      window.removeEventListener('scroll', updateMousePosition, true);
      resizeObserver.disconnect();
    };
  }, [ref, updateMousePosition]);
  const gradientPosition = useMemo(() => {
    const div = ref.current;
    if (!div) return {
      x: '-200%',
      y: '-200%'
    } as const;
    const rect = div.getBoundingClientRect();
    const xRaw = smoothedMousePosition.x - rect.left;
    const yRaw = smoothedMousePosition.y - rect.top;

    // Bias toward the center so the effect is more subtle (otherwise, moving vertically can cause some fairly strong shifts)

    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const deltaX = xRaw - centerX;
    const deltaY = yRaw - centerY;
    const distanceFromCenter = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const maxDistance = Math.sqrt(centerX * centerX + centerY * centerY);
    const repulsionStrength = -0.4;
    const repulsionFactor = 1 + repulsionStrength * (1 - distanceFromCenter / maxDistance);
    const x = centerX + deltaX * repulsionFactor;
    const y = centerY + deltaY * repulsionFactor;
    return {
      x,
      y
    };
  }, [ref, smoothedMousePosition]);
  return gradientPosition;
}
;
interface GradientDivProps {
  readonly children?: ReactNode;
  readonly className?: string;
  readonly innerClassName?: string;
  readonly appliedToBothClassName?: string;
}
const _exp2 = /*#__PURE__*/() => p => `${p.gradientPosition.x}px ${p.gradientPosition.y}px`;
const BorderDivBaseWrapper = /*#__PURE__*/styled('div')({
  name: "BorderDivBaseWrapper",
  class: "b1ajsnxj",
  propsAsIs: false,
  vars: {
    "b1ajsnxj-0": [_exp2()]
  }
});
const BorderDivBase = /*#__PURE__*/styled('div')({
  name: "BorderDivBase",
  class: "bxjuzgd",
  propsAsIs: false
});
export function ShinyBorderCard({
  children,
  className,
  appliedToBothClassName,
  innerClassName
}: GradientDivProps) {
  const ref = React.useRef<HTMLDivElement>(null);
  const gradientPosition = useGradientPosition(ref);
  return <BorderDivBaseWrapper ref={ref} gradientPosition={gradientPosition} className={cx(appliedToBothClassName, className)}>
		<BorderDivBase className={cx(appliedToBothClassName, innerClassName)}>
			{children}
		</BorderDivBase>
	</BorderDivBaseWrapper>;
}
;